<script setup>
import {
  ref, onMounted, computed, watch,
} from 'vue';
import { isEmpty } from 'lodash';
import axios from 'axios';
import { dateToLocaleDateString } from '../../../libs/dates';
import { gestionarIncompatibles } from '../../../libs/checkboxes';

const props = defineProps({
  cancelarUrl: { type: String, default: null },
});

const agrupaciones = ref([]);
const ejercicios = ref([]);
const infoCentros = ref([]);
const comunidades = ref([]);

const agrupacion = ref();
const ejercicio = ref();
const comunidadAutonoma = ref();
const autorizacionCentro = ref();
const fechaFinAutorizacion = ref();
const nifTitularAutorizacion = ref();
const conCabeceras = ref(false);

const declaracionesSeleccionadas = ref([]);

const cargando = ref(false);
const mainForm = ref();
const form = ref(null);
const generando = ref(false);

const puedeGenerar = computed(() => (
  declaracionesSeleccionadas.value.length > 1
    && comunidadAutonoma.value
));

const urlConjuntaExportar = '/modelo233/declaraciones/conjunta_exportar';

async function loadConjuntaInfo() {
  const params = {
    agrupacion_id: agrupacion.value?.id,
    ejercicio: ejercicio.value,
  };

  const { data } = await axios.get('/modelo233/declaraciones/conjunta_info', { params });
  agrupaciones.value = data.agrupaciones;
  ejercicios.value = data.ejercicios;
  infoCentros.value = data.info_centros || [];
  comunidades.value = data.comunidades || [];
}

const toggleDeclaracionSeleccionada = (declaracion, checked) => {
  if (!checked) {
    declaracionesSeleccionadas.value.splice(
      declaracionesSeleccionadas.value.indexOf(declaracion.id), 1,
    );
  } else {
    declaracionesSeleccionadas.value.push(declaracion.id);
  }
};

function anadirFormInputs(inputName, values, inputType) {
  values.forEach((value) => {
    const input = document.createElement('input');
    input.setAttribute('type', inputType || 'hidden');
    input.setAttribute('name', inputName);
    input.setAttribute('value', value);
    form.value.appendChild(input);
  });
}

async function exportar() {
  if (agrupacion.value == null || ejercicio.value == null
      || declaracionesSeleccionadas.value == null) {
    return false;
  }

  generando.value = true;
  try {
    anadirFormInputs('agrupacion_id', [agrupacion.value.id]);
    anadirFormInputs('ejercicio', [ejercicio.value]);
    anadirFormInputs('declaracion_id[]', declaracionesSeleccionadas.value);
    anadirFormInputs('comunidad_autonoma_id', [comunidadAutonoma.value.id]);
    anadirFormInputs('autorizacion_centro', [autorizacionCentro.value]);
    if (fechaFinAutorizacion.value) {
      anadirFormInputs('fecha_fin_autorizacion', [fechaFinAutorizacion.value]);
    }
    if (nifTitularAutorizacion.value) {
      anadirFormInputs('nif_titular_autorizacion', [nifTitularAutorizacion.value]);
    }
    anadirFormInputs('con_cabeceras', [conCabeceras.value]);

    form.value.submit();
    return true;
  } finally {
    generando.value = false;
  }
}

watch([agrupacion, ejercicio], loadConjuntaInfo);

onMounted(async () => {
  cargando.value = true;

  agrupacion.value = null;
  ejercicio.value = null;
  declaracionesSeleccionadas.value = [];
  infoCentros.value = [];
  comunidadAutonoma.value = null;
  autorizacionCentro.value = null;
  fechaFinAutorizacion.value = null;
  nifTitularAutorizacion.value = null;

  gestionarIncompatibles(mainForm.value);

  try {
    await loadConjuntaInfo();

    if (agrupaciones.value.length) {
      [agrupacion.value] = agrupaciones.value;
    }
    if (ejercicios.value.length) {
      [ejercicio.value] = ejercicios.value;
    }
  } finally {
    cargando.value = false;
  }
});

</script>

<template id="declaracion-conjunta-form-template">
  <div ref="mainForm">
    <form
      ref="form"
      method="post"
      :action="urlConjuntaExportar"
    >
      <InputCsrf />
    </form>
    <div class="row">
      <div class="col-sm-8">
        <div class="form-group">
          <label class="control-label">
            {{ $t('agrupacion') }}
          </label>
          <select-cifra
            v-model="agrupacion"
            :options="agrupaciones"
            :searchable="false"
            label="nombre"
            track-by="id"
            :show-labels="false"
            :allow-empty="false"
          ></select-cifra>
        </div>
      </div>
      <div class="col-sm-4">
        <div class="form-group">
          <label class="control-label">
            {{ $t('ejercicio') }}
          </label>
          <select-cifra
            v-model="ejercicio"
            :options="ejercicios"
            :searchable="false"
            :show-labels="false"
            :allow-empty="false"
          ></select-cifra>
        </div>
      </div>
    </div>
    <div class="row">
      <div class="col-sm-2">
        <input-base
          ref="inputAutorizacionCentro"
          v-model="autorizacionCentro"
          :label-key="$I18n.t('activerecord.attributes.modelo233/declaracion.autorizacion_centro')"
        />
      </div>
      <div class="col-sm-3">
        <div class="form-group">
          <label for="" class="control-label required">
            <abbr title="requerido">*</abbr>
            {{ $I18n.t('comunidad_autonoma') }}
          </label>
          <select-cifra
            v-model="comunidadAutonoma"
            :placeholder="$t('Seleccione')"
            :options="comunidades"
            :searchable="true"
            :show-labels="false"
            :allow-empty="false"
            label="nombre"
            track-by="value"
          />
        </div>
      </div>
      <div class="col-sm-2">
        <div class="form-group">
          <input-fecha-hora
            v-model="fechaFinAutorizacion"
            :label="$I18n.t(
              'activerecord.attributes.modelo233/declaracion.fecha_fin_autorizacion'
            )"
          />
        </div>
      </div>
      <div class="col-sm-3">
        <input-base
          ref="inputnifTitularAutorizacion"
          v-model="nifTitularAutorizacion"
          :label-key="$I18n.t(
            'activerecord.attributes.modelo233/declaracion.nif_titular_autorizacion'
          )"
        />
      </div>
      <div class="col-sm-2 m-t-lg">
        <input-checkbox-boolean v-model="conCabeceras">
          {{ $t('con_cabeceras') }}
        </input-checkbox-boolean>
      </div>
    </div>
    <div v-if="!isEmpty(infoCentros)">
      <div class="titulo-seccion ">
        {{ $t('declaraciones_consolidar') }}
      </div>
      <table class="table table-striped cifra-table cifra-table-gris">
        <thead>
          <tr>
            <th>{{ $t('Centro') }}</th>
            <th class="col-min">
              {{ $t('activerecord.attributes.modelo233/declaracion.fecha_presentacion') }}
            </th>
            <th class="col-min">
              {{ $t('Total_registros') }}
            </th>
            <th class="col-min">
              {{ $t('seleccion') }}
            </th>
          </tr>
        </thead>
        <tbody>
          <template v-for="centro in infoCentros" :key="centro.id">
            <tr v-for="dec in centro.declaraciones" :key="dec.id">
              <td>{{ centro.nombre }}</td>
              <td>
                {{ dec.fecha_presentacion ? dateToLocaleDateString(dec.fecha_presentacion) : '' }}
              </td>
              <td>{{ dec.lineas.length }}</td>
              <td class="text-center">
                <input-checkbox-boolean
                  tipo="switch"
                  name="declaracion_id[]"
                  :value="dec.id"
                  @update:model-value="toggleDeclaracionSeleccionada(dec, $event)"
                />
              </td>
            </tr>
          </template>
        </tbody>
      </table>
    </div>
    <div class="form-actions">
      <a v-if="props.cancelarUrl" class="btn btn-default" :href="props.cancelarUrl">
        {{ $I18n.t('Cancelar') }}
      </a>
      <boton-guardar
        :submitting="generando"
        :label="$I18n.t('Generar')"
        :disabled="!puedeGenerar"
        @click.prevent="exportar()"
      ></boton-guardar>
    </div>
  </div>
</template>
